import type { MergerRoutePriority, NodeConnections } from '../../types/nodeConfig';
import { formatRouteLabel } from '../../lib/routeLabels';

interface Props {
  node: Record<string, unknown>;
  connections: NodeConnections;
  readOnly?: boolean;
  onChange: (patch: Record<string, unknown>) => void;
}

export default function MergerNodeConfig({
  node,
  connections,
  readOnly,
  onChange,
}: Props) {
  const mode = String(node.mode ?? 'fifo');
  const incoming = connections.incoming;
  const routePriorities =
    (node.route_priorities as MergerRoutePriority[] | undefined) ?? [];

  const showPriorities =
    mode === 'priority_by_route' || mode === 'priority_by_product';

  const ensurePriorities = (): MergerRoutePriority[] => {
    if (routePriorities.length === incoming.length) return routePriorities;
    return incoming.map((r, i) => ({
      route_id: r.route_id,
      priority: routePriorities.find((p) => p.route_id === r.route_id)?.priority ?? i,
    }));
  };

  const priorities = ensurePriorities();

  return (
    <section className="space-y-4 rounded-lg border border-border bg-surface/40 p-4">
      <h3 className="panel-section-title">Merger configuration</h3>
      <p className="text-sm text-text-muted">
        Multiple incoming streams combine and leave through one outgoing route in
        the configured order.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-text-muted">Merge mode</label>
          <select
            className="input-field w-full"
            disabled={readOnly}
            value={mode}
            onChange={(e) => onChange({ mode: e.target.value })}
          >
            <option value="fifo">FIFO arrival order</option>
            <option value="round_robin">Round robin by route</option>
            <option value="priority_by_route">Priority by route</option>
            <option value="priority_by_product">Priority by product type</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-text-muted">
            Queue warning threshold
          </label>
          <input
            type="number"
            min={0}
            className="input-field w-full"
            disabled={readOnly}
            value={Number(node.queue_warning_threshold ?? '')}
            onChange={(e) =>
              onChange({
                queue_warning_threshold: e.target.value
                  ? Number(e.target.value)
                  : undefined,
              })
            }
            placeholder="e.g. 10"
          />
        </div>
      </div>

      {showPriorities && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-text-muted">Route priorities</p>
          <p className="text-[10px] text-text-subtle">
            Lower numbers are served first when merging.
          </p>
          <table className="w-full text-left text-xs">
            <thead className="text-text-subtle">
              <tr>
                <th className="pb-2 pr-2">Incoming route</th>
                <th className="pb-2">Priority</th>
              </tr>
            </thead>
            <tbody>
              {priorities.map((entry, index) => {
                const route = incoming.find((r) => r.route_id === entry.route_id);
                return (
                  <tr key={entry.route_id} className="border-t border-border/60">
                    <td className="py-2 pr-2 text-text">
                      {route ? formatRouteLabel(route, 'incoming') : entry.route_id}
                    </td>
                    <td className="py-2">
                      <input
                        type="number"
                        className="input-field w-20 py-1"
                        disabled={readOnly}
                        value={entry.priority}
                        onChange={(e) => {
                          const next = ensurePriorities();
                          next[index] = {
                            ...entry,
                            priority: Number(e.target.value),
                          };
                          onChange({ route_priorities: next });
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {incoming.length === 0 && (
        <p className="text-xs text-warning-on-dark">
          No incoming routes connected — merger requires at least one incoming route.
        </p>
      )}
      {connections.outgoing.length === 0 && (
        <p className="text-xs text-warning-on-dark">
          No outgoing route connected — merger requires one outgoing route.
        </p>
      )}
    </section>
  );
}
